import { call, takeLatest } from 'redux-saga/effects';
import {
  addClinicalTrialQuestionnaire,
  assignClinicalTrialQuestionnaireToVisit,
  deleteClinicalTrialQuestionnaire,
  fetchClinicalTrialQuestionnaireList,
  fetchQuestionnaireList,
  unassignClinicalTrialQuestionnaireFromVisit,
} from '@api/questionnaire';
import { actions } from './questionnaires.slice';
import { Resp, dispatch } from '../helpers';
import {
  AddClinicalTrialQuestionnaireAction,
  AssignClinicalTrialQuestionnaireAction,
  DeleteClinicalTrialQuestionnaireAction,
  FetchClinicalTrialQuestionnairesAction,
  FetchQuestionnairesAction,
  UnassignClinicalTrialQuestionnaireAction,
} from '@restore/questionnaires/questionnaires.types';
import { runToast, showErrorToast } from '@restore/tools';
import { fetchClinicalTrial } from '@restore/clinicalTrial/clinicalTrial.slice';
import { actions as activateActions } from '../activate/activateClinicalTrialSlice';

export function* questionnairesWorker(action: FetchQuestionnairesAction) {
  try {
    const questionnaires: Resp<typeof fetchQuestionnaireList> = yield call(
      fetchQuestionnaireList,
      action.payload.name
    );
    yield dispatch(
      actions.fetchQuestionnairesSuccess(questionnaires?.data || [])
    );
  } catch (error) {
    yield dispatch(actions.fetchQuestionnairesFailure(error as Error));
  }
}
export function* fetchClinicalTrialQuestionnairesWorker(
  action: FetchClinicalTrialQuestionnairesAction
) {
  try {
    const questionnaires: Resp<typeof fetchClinicalTrialQuestionnaireList> =
      yield call(fetchClinicalTrialQuestionnaireList, action.payload?.id);
    yield dispatch(
      actions.fetchClinicalTrialQuestionnairesSuccess(questionnaires.data)
    );
  } catch (error) {
    yield dispatch(
      actions.fetchClinicalTrialQuestionnairesFailure(error as Error)
    );
  }
}

export function* addClinicalTrialQuestionnaireWorker(
  action: AddClinicalTrialQuestionnaireAction
) {
  try {
    yield call(
      addClinicalTrialQuestionnaire,
      action.payload.id,
      action.payload.data
    );
    yield call(runToast, 'success', 'Template added successfully');
    yield call(fetchClinicalTrialQuestionnairesWorker, action);
    yield dispatch(actions.addClinicalTrialQuestionnaireSuccessAction({}));
  } catch (error) {
    yield call(showErrorToast, error, 'Failed to add template');
    yield dispatch(actions.addClinicalTrialQuestionnaireFailureAction(error));
  }
}

export function* deleteClinicalTrialQuestionnaireWorker(
  action: DeleteClinicalTrialQuestionnaireAction
) {
  try {
    yield call(
      deleteClinicalTrialQuestionnaire,
      action.payload.id,
      action.payload.questionnaireId
    );
    yield call(runToast, 'success', 'Template deleted successfully');
    yield call(fetchClinicalTrialQuestionnairesWorker, action);
    yield dispatch(actions.deleteClinicalTrialQuestionnaireSuccessAction({}));
  } catch (error) {
    yield call(showErrorToast, error, 'Failed to delete template');
    yield dispatch(
      actions.deleteClinicalTrialQuestionnaireFailureAction(error)
    );
  }
}
export function* assignClinicalTrialQuestionnaireWorker(
  action: AssignClinicalTrialQuestionnaireAction
) {
  try {
    yield call(
      (id, data) => assignClinicalTrialQuestionnaireToVisit(id, data),
      action.payload.id,
      action.payload.data
    );
    yield call(runToast, 'success', 'Form assigned successfully');
    yield dispatch(fetchClinicalTrial(action.payload.id));
    yield dispatch(actions.assignClinicalTrialQuestionnaireSuccessAction({}));
    yield dispatch(activateActions.setErrors({ name: 'forms', errors: [] }));
  } catch (error) {
    yield call(showErrorToast, error, 'Failed to assign form');
    yield dispatch(
      actions.assignClinicalTrialQuestionnaireFailureAction(error)
    );
  }
}
export function* unassignClinicalTrialQuestionnaireWorker(
  action: UnassignClinicalTrialQuestionnaireAction
) {
  try {
    yield call(
      unassignClinicalTrialQuestionnaireFromVisit,
      action.payload.id,
      action.payload.questionnaireId
    );
    yield call(runToast, 'success', 'Form unassigned successfully');
    yield dispatch(fetchClinicalTrial(action.payload.id));
    yield dispatch(actions.unassignClinicalTrialQuestionnaireSuccessAction({}));
  } catch (error) {
    yield call(showErrorToast, error, 'Failed to unassign form');
    yield dispatch(
      actions.unassignClinicalTrialQuestionnaireFailureAction(error)
    );
  }
}

export function* questionnairesSaga() {
  yield takeLatest(actions.fetchQuestionnaires.type, questionnairesWorker);
  yield takeLatest(
    actions.fetchClinicalTrialQuestionnaires.type,
    fetchClinicalTrialQuestionnairesWorker
  );
  yield takeLatest(
    actions.addClinicalTrialQuestionnaireAction.type,
    addClinicalTrialQuestionnaireWorker
  );
  yield takeLatest(
    actions.deleteClinicalTrialQuestionnaireAction.type,
    deleteClinicalTrialQuestionnaireWorker
  );
  yield takeLatest(
    actions.assignClinicalTrialQuestionnaireAction.type,
    assignClinicalTrialQuestionnaireWorker
  );
  yield takeLatest(
    actions.unassignClinicalTrialQuestionnaireAction.type,
    unassignClinicalTrialQuestionnaireWorker
  );
}
